import { motion, useReducedMotion, type MotionProps } from 'framer-motion';
import { useMemo, type ElementType, type ReactNode } from 'react';
import {
  fadeIn,
  fadeInUp,
  drawLine,
  sectionViewport,
} from '../animations';

type RevealVariant = 'fadeInUp' | 'fadeIn' | 'drawLine';

const VARIANTS = {
  fadeInUp,
  fadeIn,
  drawLine,
} as const;

export type RevealProps = {
  children?: ReactNode;
  as?: ElementType;
  variant?: RevealVariant;
  delay?: number;
  className?: string;
} & MotionProps;

/**
 * The atomic scroll-reveal wrapper. Animates ONCE on inView
 * (sectionViewport: {once:true, margin:-80px}) then holds still.
 * Under prefers-reduced-motion it collapses to an instant ~0.2s
 * opacity fade with no transform.
 */
export function Reveal({
  children,
  as = 'div',
  variant = 'fadeInUp',
  delay = 0,
  className,
  transition,
  ...rest
}: RevealProps) {
  const reduce = useReducedMotion();
  const MotionTag = useMemo(() => motion(as as ElementType), [as]);

  if (reduce) {
    return (
      <MotionTag
        className={className}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={sectionViewport}
        transition={{ duration: 0.2, delay: 0 }}
        {...rest}
      >
        {children}
      </MotionTag>
    );
  }

  return (
    <MotionTag
      className={className}
      variants={VARIANTS[variant]}
      initial="hidden"
      whileInView="visible"
      viewport={sectionViewport}
      transition={delay ? { delay } : transition}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
